import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { formatApiError } from "../lib/api";
import { Loader2 } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user === undefined) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="login-page">
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-10">
          <img src="/brand/mak-logo.png" alt="MAK" className="h-40 w-40 object-contain mb-4" />
        </div>

        <div className="mb-8 text-center">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Anmeldung</div>
          <h2 className="text-2xl font-semibold tracking-tight text-zinc-900">
            Willkommen zurück
          </h2>
          <p className="text-sm text-zinc-500 mt-1">
            Melden Sie sich mit Ihren Zugangsdaten an.
          </p>
        </div>

        <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-600 block mb-1.5">
              E-Mail
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="login-email-input"
              placeholder="name@firma.de"
              className="w-full px-3.5 py-2.5 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black transition-all"
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-widest text-zinc-600 block mb-1.5">
              Passwort
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="login-password-input"
              placeholder="••••••••"
              className="w-full px-3.5 py-2.5 bg-white border border-zinc-300 rounded-sm text-sm focus:border-black focus:ring-1 focus:ring-black transition-all"
            />
          </div>

          {error && (
            <div className="text-xs font-medium text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded-sm" data-testid="login-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white font-medium text-sm py-2.5 rounded-sm transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {loading ? <Loader2 size={14} className="animate-spin"/> : null}
            Anmelden
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-zinc-200 text-xs text-zinc-500 text-center">
          Probleme bei der Anmeldung? Wenden Sie sich an Ihren MAK-Ansprechpartner.
        </div>
      </div>
    </div>
  );
}
